import React from 'react';

import { tv } from '../../tv';

/**
 * Styled ground for FOREIGN markup: the semantic elements a markdown or
 * Lexical renderer emits (headings, paragraphs, lists, tables, code,
 * blockquotes). Prose styles what a renderer produced; it never parses.
 *
 * Scope: rendered content only. Hand-written UI keeps Text and Heading,
 * which own their semantics one element at a time; reaching for Prose to
 * dodge them is misuse, because it swaps compile-checked props for
 * whatever-the-markup-says.
 *
 * Children, never an html string. A string prop would be an invitation to
 * innerHTML, and sanitisation belongs to the renderer that parsed the
 * untrusted source, not to a styling component that cannot know where the
 * string came from. The renderer hands Prose React elements it already
 * vouches for.
 *
 * The styling is descendant utilities in this recipe (ck:[&_h1]:...),
 * compiled into the theme like every other utility, so check-utilities
 * verifies each rule is emitted. One selector per element, deliberately:
 * selectors carrying commas or parens (:is(), :not()) break the emitted
 * selector parsing in that gate, so pre code is overridden by specificity
 * instead of excluded with :not(). See prose.md for the full decision.
 */

const proseStyles = tv({
  base: [
    'ck:font-sans ck:text-text-default ck:break-words',
    /* The first and last block sit flush against whatever holds the prose;
     * outer margins belong to the composition, not the content. Trimmed one
     * wrapper level deep as well, because Streamdown and Lexical render the
     * blocks inside wrapper elements, so the real first block is the first
     * child of the first child (the streaming-markdown precedent). */
    'ck:[&>*:first-child]:mt-0 ck:[&>*:last-child]:mb-0',
    'ck:[&>*:first-child>*:first-child]:mt-0 ck:[&>*:last-child>*:last-child]:mb-0',
    /* Headings share Heading's weight; their steps come from the size
     * variant below. More space above than below binds a heading to the
     * content it introduces. */
    'ck:[&_h1]:font-semibold ck:[&_h1]:mt-6 ck:[&_h1]:mb-2',
    'ck:[&_h2]:font-semibold ck:[&_h2]:mt-6 ck:[&_h2]:mb-2',
    'ck:[&_h3]:font-semibold ck:[&_h3]:mt-4 ck:[&_h3]:mb-2',
    'ck:[&_h4]:font-semibold ck:[&_h4]:mt-4 ck:[&_h4]:mb-1',
    'ck:[&_p]:my-3',
    /* Preflight zeroes list padding and the theme restores only the marker,
     * so the indent is set here. */
    'ck:[&_ul]:my-3 ck:[&_ul]:pl-6',
    'ck:[&_ol]:my-3 ck:[&_ol]:pl-6',
    'ck:[&_li]:my-1',
    'ck:[&_a]:text-accent-11 ck:[&_a]:underline ck:[&_a:hover]:no-underline',
    /* Explicit rather than left to the browser: the coexisting Chakra reset
     * zeroes emphasis, and these unlayered utilities win it back. */
    'ck:[&_strong]:font-semibold ck:[&_em]:italic',
    /* The inline chip, matching Code: mono at 0.9em of the surrounding text
     * on a quiet tint. */
    'ck:[&_code]:font-mono ck:[&_code]:text-code ck:[&_code]:bg-neutral-a-3',
    'ck:[&_code]:rounded-2 ck:[&_code]:px-1',
    'ck:[&_pre]:my-3 ck:[&_pre]:overflow-x-auto ck:[&_pre]:rounded-3',
    'ck:[&_pre]:border ck:[&_pre]:border-border-subtle ck:[&_pre]:bg-surface-recessed',
    'ck:[&_pre]:p-3 ck:[&_pre]:font-mono ck:[&_pre]:text-sm',
    /* Inside a block the chip dissolves: the pre carries the surface, and
     * this higher-specificity selector beats the [&_code] chip above. */
    'ck:[&_pre_code]:bg-transparent ck:[&_pre_code]:px-0',
    'ck:[&_blockquote]:my-3 ck:[&_blockquote]:border-l-2 ck:[&_blockquote]:border-border-default',
    'ck:[&_blockquote]:pl-3 ck:[&_blockquote]:text-text-muted',
    'ck:[&_table]:my-3 ck:[&_table]:w-full ck:[&_table]:border-collapse',
    'ck:[&_th]:border ck:[&_th]:border-border-divider ck:[&_th]:bg-neutral-a-3',
    'ck:[&_th]:px-2 ck:[&_th]:py-1 ck:[&_th]:font-semibold ck:[&_th]:text-left',
    'ck:[&_td]:border ck:[&_td]:border-border-divider ck:[&_td]:px-2 ck:[&_td]:py-1',
    'ck:[&_hr]:my-6 ck:[&_hr]:border-border-divider',
  ],
  variants: {
    /* Each step sets the body and a heading ladder from the closed type
     * scale. base reads as a document; sm is dense rendering (chat
     * messages, previews), where the ladder drops so an h1 in a bubble
     * does not shout. */
    size: {
      base: [
        'ck:text-base',
        'ck:[&_h1]:text-2xl ck:[&_h2]:text-xl ck:[&_h3]:text-lg ck:[&_h4]:text-base',
      ],
      sm: [
        'ck:text-sm',
        'ck:[&_h1]:text-lg ck:[&_h2]:text-base ck:[&_h3]:text-sm ck:[&_h4]:text-xs',
      ],
    },
  },
  defaultVariants: {
    size: 'base',
  },
});

export interface ProseProps {
  /** Rendered markup (markdown, Lexical, Streamdown output) as React children. */
  children?: React.ReactNode;
  /** Body step and heading ladder. `sm` is for dense rendering such as chat. */
  size?: 'sm' | 'base';
  id?: string;
}

export const Prose = React.forwardRef<HTMLDivElement, ProseProps>(function Prose(
  { children, size = 'base', ...props },
  ref,
) {
  return (
    <div {...props} ref={ref} className={proseStyles({ size })} data-component="prose">
      {children}
    </div>
  );
});
